import React, { useState } from "react";
import { Box, Button, Grid, Typography, Radio } from "@mui/material";
import { useDispatch, useSelector } from "react-redux";

const RenewalSelectRequest = ({ 
  handleRadioChange, 
  renewalType, 
  renewalTypeOptions,
  subRenewalTypeOptions = [],
  subRenewalType,
  handleSubRadioChange,
  policyRenew
}) => {
  const shouldShowSubOptions = (
    (["PF2", "PF3", "PTF"].includes(policyRenew?.contract_type) && policyRenew?.is_full_declaration !== "Y") ||
    (policyRenew?.is_full_declaration !== "Y" && renewalType === "RENEW")
  );

  return (
    <>
      <Box
        sx={{
          overflow: "hidden",
          backgroundColor: "white",
          padding: "30px 30px 35px 30px",
          boxShadow: "-2px -2px 20px 0 rgba(0,0,0,0.1)"
        }}
      >
        <Grid
          sx={{ padding: "0px 0px 20px 0px", display: "flex", justifyContent: "space-between", alignItems: "center" }}
        >
          <Typography sx={{ fontSize: "18px", fontWeight: 500 }}>Policy chosen for renewal</Typography>
        </Grid>

        <Grid container rowSpacing={1} columnSpacing={3} item xs={5}>
          <Grid item xs={6}>
            <Typography sx={{ fontSize: "16px", fontWeight: 600 }}>Company:</Typography>
          </Grid>
          <Grid item xs={6}>
            <Typography sx={{ fontSize: "16px", fontWeight: 500 }}>{policyRenew.company_name}</Typography>
          </Grid>
          <Grid item xs={6}>
            <Typography sx={{ fontSize: "16px", fontWeight: 600 }}>Reference / Policy No.:</Typography>
          </Grid>
          <Grid item xs={6}>
            <Typography sx={{ fontSize: "16px", fontWeight: 500 }}>{policyRenew.policy_no}</Typography>
          </Grid>
          <Grid item xs={6}>
            <Typography sx={{ fontSize: "16px", fontWeight: 600 }}>Policy Expiry Date:</Typography>
          </Grid>
          <Grid item xs={6}>
            <Typography sx={{ fontSize: "16px", fontWeight: 500 }}>{policyRenew.expiry_date}</Typography>
          </Grid>
        </Grid>
      </Box>
      <Box
        sx={{
          overflow: "hidden",
          backgroundColor: "white",
          padding: "30px 30px 35px 30px",
          boxShadow: "-2px -2px 20px 0 rgba(0,0,0,0.1)",
          marginTop: "20px",
          marginBottom: "80px"
        }}
      >
        <Grid sx={{ display: "flex", justifyContent: "space-between" }}>
          <Typography sx={{ padding: "0px 0px 20px 0px", fontSize: "18px", fontWeight: 500 }}>
            Select your request
          </Typography>
        </Grid>

        {/* Display note when isFullDeclaration === "Y" */}
        {policyRenew?.is_full_declaration === "Y" && (
          <Typography 
            sx={{ 
              padding: "0px 0px 20px 0px", 
              fontSize: "14px", 
              fontWeight: 400,
              color: "red",
              lineHeight: "1.5",
              marginTop: "4px"
            }}
          >
            Note - Full AML declaration has not been submitted in the last 2 years; therefore, ACRA, MAS314, and Authorized Signatory's ID documents are mandatory for this renewal.
          </Typography>
        )}

        {
        <Grid container rowSpacing={1} columnSpacing={3} item>
          {renewalTypeOptions.map((item, index) => (
            <React.Fragment key={index}>
              <Grid item xs={12} style={{ display: "flex", alignItems: "center" }}>
                <Radio
                  checked={renewalType === item.value}
                  onChange={event => {
                    handleRadioChange(event, {});
                  }}
                  value={item.value}
                  name="renewal-type"
                  color="primary"
                />
                <Typography sx={{ fontSize: "16px", fontWeight: 500 }}>{item.name}</Typography>
              </Grid>
              
              {/* Show sub-options when conditions are met */}
              {shouldShowSubOptions && 
               renewalType === item.value && 
               (item.value === "RENEW_WITH_CHG" || item.value === "RENEW_WITHOUT_CHG" || item.value === "RENEW") && 
               subRenewalTypeOptions.length > 0 && (
                <Grid item xs={12} sx={{ paddingLeft: "40px !important" }}>
                  <Grid container rowSpacing={1}>
                    {subRenewalTypeOptions.map((subItem, subIndex) => (
                      <Grid item xs={12} style={{ display: "flex", alignItems: "flex-start" }} key={subIndex}>
                        <Radio
                          checked={subRenewalType === subItem.value}
                          onChange={handleSubRadioChange}
                          value={subItem.value}
                          name="sub-renewal-type"
                          color="primary"
                          sx={{ paddingTop: "2px" }}
                        />
                        <Typography sx={{ fontSize: "14px", fontWeight: 400, paddingTop: "9px", lineHeight: "1.5" }}>
                          {subItem.name}
                        </Typography>
                      </Grid>
                    ))}
                  </Grid>
                </Grid>
              )}
            </React.Fragment>
          ))}
        </Grid>
        }

        {policyRenew.contract_type === "PF3" && (
          <Box sx={{ marginTop: "30px", backgroundColor: "#f9f9f9", padding: "25px", borderRadius: "4px" }}>
            <Typography sx={{ fontSize: "16px", fontWeight: "bold", marginBottom: "20px" }}>
              Renewal Guidelines
            </Typography>

            {/* Section A: Renew without changes */}
            <Box sx={{ marginBottom: "20px" }}>
              <Typography sx={{ fontSize: "16px", fontWeight: "bold" }}>
                (A) Renew without changes (To renew as per existing policy)
              </Typography>

              <Typography sx={{ fontSize: "16px", fontWeight: "normal", marginTop: "10px", textDecoration: "underline" }}>
                Definition of 'without changes':
              </Typography>
              <Box component="ul" sx={{ fontSize: "16px", marginTop: "5px", marginBottom: "5px", paddingLeft: "40px" }}>
                <li>No changes to existing benefits, categories and member listing</li>
              </Box>

              <Typography sx={{ fontSize: "16px", fontWeight: "bold", marginTop: "5px", marginBottom: "5px" }}>
                And
              </Typography>

              <Box component="ul" sx={{ fontSize: "16px", marginTop: "5px", marginBottom: "10px", paddingLeft: "40px" }}>
                <li>No changes to ACRA, MAS314 and HR Contact Name from the previous declaration</li>
              </Box>

              <Typography sx={{ fontSize: "16px", fontWeight: "bold", marginTop: "10px" }}>
                Required Forms
              </Typography>
              <Box component="ol" sx={{ fontSize: "16px", marginTop: "5px", paddingLeft: "40px", marginBottom: "10px" }}>
                <li>Renewal Invitation Letter</li>
                <li>
                  HR Declaration on Shareholders and Directorship
                  <br />
                  <span>
                    (ACRA and MAS314 are not required if there are no changes from the last declaration)
                  </span>
                </li>
                <li>Declaration of Entitlement to Claim Input Tax on Insurance Policy</li>
                <li>Declaration of Specific Individual</li>
              </Box>
            </Box>

            {/* Section B: Renew with changes */}
            <Box sx={{ marginBottom: "20px" }}>
              <Typography sx={{ fontSize: "16px", fontWeight: "bold" }}>
                (B) Renew with changes
              </Typography>

              <Typography sx={{ fontSize: "16px", fontWeight: "normal", marginTop: "10px", textDecoration: "underline" }}>
                Definition of 'with changes':
              </Typography>
              <Box component="ul" sx={{ fontSize: "16px", marginTop: "5px", marginBottom: "10px", paddingLeft: "40px" }}>
                <li>Changes in benefits and/or categories</li>
                <li>Changes in member listing (addition, deletion, edit)</li>
                <li>Changes in HR contact name from previous declaration</li>
                <li>Changes in ACRA and/or MAS314</li>
              </Box>

              <Typography sx={{ fontSize: "16px", fontWeight: "bold", marginTop: "10px" }}>
                Required Forms
              </Typography>
              <Box sx={{ fontSize: "16px", marginTop: "5px", marginBottom: "10px" }}>
                <Typography sx={{ fontSize: "16px", marginBottom: "6px" }}>1. Renewal Invitation Letter</Typography>
                <Typography sx={{ fontSize: "16px", marginBottom: "6px" }}>
                  2.1 If there is no change to ACRA, MAS314 and HR contact name from the last declaration, please
                  submit HR Declaration on Shareholders and Directorship
                </Typography>
                <Typography sx={{ fontSize: "16px", fontWeight: "bold", textDecoration: "underline", margin: "10px 0" }}>
                  Or
                </Typography>
                <Typography sx={{ fontSize: "16px", marginBottom: "6px" }}>
                  2.2 If there is change to ACRA, MAS314 and/or HR contact name from the last declaration, please
                  submit latest ACRA, MAS314 and NRIC/FIN front and back of all authorised persons
                </Typography>
                <Typography sx={{ fontSize: "16px", marginBottom: "6px" }}>
                  3. Declaration of Entitlement to Claim Input Tax on Insurance Policy
                </Typography>
                <Typography sx={{ fontSize: "16px", marginBottom: "6px" }}>
                  4. Declaration of Specific Individual
                </Typography>
                <Typography sx={{ fontSize: "16px" }}>
                  5. New member listing (Only applicable if the date of employment and/or last date of employment with
                  the company occurs after the policy renewal date)
                </Typography>
              </Box>

              <Typography sx={{ fontSize: "16px", fontWeight: "bold", marginTop: "15px" }}>Note:</Typography>
              <Box component="ul" sx={{ fontSize: "16px", marginTop: "5px", paddingLeft: "40px", marginBottom: "10px" }}>
                <li>
                  For changes in benefits and/or categories, please state the instructions clearly in the Renewal
                  Invitation Letter Page 4
                </li>
                <li>
                  For changes in benefits and/or categories, please provide the updated member listing indicating the
                  category name and plan number for each insured line of coverage
                </li>
                <li>
                  If date of employment and/or last date of employment with the company occurs before the policy renewal
                  date, please inform HR to update via HR PRUWorks
                </li>
              </Box>
            </Box>
          </Box>
        )}
      </Box>
    </>
  );
};

export default RenewalSelectRequest;
